import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { EntityThumbnail } from '../components/EntityImage';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Wrench, Plus, X } from 'lucide-react';

const ACCESSORY_TYPES = ['Optic', 'Magazine', 'Holster', 'Light', 'Laser', 'Sling', 'Suppressor', 'Grip', 'Stock', 'Rail', 'Bipod', 'Case', 'Cleaning Kit', 'Other'];

export default function AccessoriesPage() {
  const navigate = useNavigate();
  const [accessories, setAccessories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const fetchAccessories = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (typeFilter) params.append('type', typeFilter);

      const response = await api.get(`/accessories?${params.toString()}`);
      setAccessories(response.data);
    } catch (error) {
      console.error('Error fetching accessories:', error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, typeFilter]);

  useEffect(() => {
    fetchAccessories();
  }, [fetchAccessories]);

  const clearFilters = () => {
    setTypeFilter('');
    setSearchQuery('');
  };

  return (
    <Layout
      title="Accessories"
      showSearch
      onSearch={setSearchQuery}
      searchPlaceholder="Search accessories..."
    >
      <div className="space-y-6" data-testid="accessories-page">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-36 rounded-sm" data-testid="filter-type">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {ACCESSORY_TYPES.map(type => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {typeFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-muted-foreground"
              >
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
          </div>

          <Button
            onClick={() => navigate('/accessories/new')}
            className="bg-secondary hover:bg-secondary/90 rounded-sm btn-tactical"
            data-testid="add-accessory-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Accessory
          </Button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
              <div key={i} className="bg-card border border-border rounded-sm p-4 animate-pulse">
                <div className="aspect-square bg-muted rounded-sm mb-4" />
                <div className="h-4 bg-muted rounded mb-2 w-3/4" />
                <div className="h-3 bg-muted rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : accessories.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Wrench className="w-16 h-16 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Accessories Found</h3>
            <p className="text-muted-foreground mb-6 max-w-md">
              {typeFilter || searchQuery
                ? 'No accessories match your filters.'
                : 'Start tracking your firearm accessories.'}
            </p>
            {!typeFilter && !searchQuery && (
              <Button
                onClick={() => navigate('/accessories/new')}
                className="bg-secondary hover:bg-secondary/90 rounded-sm btn-tactical"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Accessory
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {accessories.map((accessory, index) => (
              <div
                key={accessory.id}
                className="bg-card border border-border rounded-sm overflow-hidden cursor-pointer card-hover animate-fade-in"
                style={{ animationDelay: `${index * 0.05}s` }}
                onClick={() => navigate(`/accessories/${accessory.id}`)}
                data-testid={`accessory-card-${accessory.id}`}
              >
                {/* Image - Using EntityThumbnail for object URL rendering */}
                <EntityThumbnail
                  imageFileIds={accessory.images}
                  alt={accessory.name}
                  containerClassName="aspect-square bg-muted overflow-hidden"
                  className="w-full h-full object-cover"
                  fallback={
                    <div className="w-full h-full flex items-center justify-center">
                      <Wrench className="w-12 h-12 text-muted-foreground/30" />
                    </div>
                  }
                />

                {/* Content */}
                <div className="p-4">
                  <h3 className="font-semibold text-foreground truncate">{accessory.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {accessory.type}
                    {accessory.brand && ` • ${accessory.brand}`}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
